/* eslint-disable no-restricted-imports */
import { useState } from 'react';

import {
  Box,
  Chip,
  Collapse,
  IconButton,
  InputAdornment,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Cancel,
  CheckCircle,
  KeyboardArrowDown,
  KeyboardArrowUp,
  LocalShipping,
  Search as SearchIcon,
} from '@material-ui/icons';

import { FormatDate } from 'components';

function ShipmentRow({ shipment }) {
  const [open, setOpen] = useState(false);

  const getStatusIcon = (status) => {
    if (status === 'ACTIVE') return <CheckCircle style={{ color: '#4CAF50' }} />;
    if (status === 'CANCELLED') return <Cancel style={{ color: '#757575' }} />;
    return null;
  };

  const getStatusLabel = (status) => {
    if (status === 'ACTIVE') return 'Ativa';
    if (status === 'CANCELLED') return 'Cancelada';
    return 'Desconhecido';
  };

  return (
    <>
      <TableRow hover>
        <TableCell>
          <IconButton size='small' onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>
        <TableCell>{shipment.shipment_number}</TableCell>
        <TableCell>
          <Chip
            label={shipment.shipping_method}
            size='small'
            color={shipment.shipping_method === 'CORREIOS' ? 'primary' : 'default'}
          />
        </TableCell>
        <TableCell>
          {shipment.shipping_method === 'CORREIOS'
            ? shipment.tracking_code
            : shipment.carrier_name || '-'}
        </TableCell>
        <TableCell>{shipment.total_volumes}</TableCell>
        <TableCell>
          {shipment.total_weight ? `${shipment.total_weight.toFixed(2)} kg` : '-'}
        </TableCell>
        <TableCell>
          <Chip
            icon={getStatusIcon(shipment.status)}
            label={getStatusLabel(shipment.status)}
            size='small'
            style={{
              backgroundColor: shipment.status === 'ACTIVE' ? '#E8F5E9' : '#F5F5F5',
            }}
          />
        </TableCell>
        <TableCell>
          <FormatDate>{shipment.created_at}</FormatDate>
        </TableCell>
        <TableCell>{shipment.created_by_name || '-'}</TableCell>
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
          <Collapse in={open} timeout='auto' unmountOnExit>
            <Box margin={2}>
              <Typography variant='h6' gutterBottom component='div'>
                Notas Fiscais Expedidas
              </Typography>
              <Table size='small'>
                <TableHead>
                  <TableRow>
                    <TableCell>Nota Fiscal</TableCell>
                    <TableCell>Pedido</TableCell>
                    <TableCell>Cliente</TableCell>
                    <TableCell>Volumes</TableCell>
                    <TableCell>Peso (kg)</TableCell>
                    <TableCell>Dimensões</TableCell>
                    <TableCell>Observações</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(shipment.items || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell>{item.invoice_number}</TableCell>
                      <TableCell>{item.order_number}</TableCell>
                      <TableCell>{item.customer_name}</TableCell>
                      <TableCell>{item.volumes}</TableCell>
                      <TableCell>{item.weight ? item.weight.toFixed(2) : '-'}</TableCell>
                      <TableCell>{item.dimensions || '-'}</TableCell>
                      <TableCell>{item.notes || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              {shipment.notes && (
                <Box mt={2}>
                  <Typography variant='subtitle2' gutterBottom>
                    Observações da Expedição:
                  </Typography>
                  <Typography variant='body2' color='textSecondary'>
                    {shipment.notes}
                  </Typography>
                </Box>
              )}
              {shipment.cancellation_reason && (
                <Box mt={2}>
                  <Typography variant='subtitle2' gutterBottom color='error'>
                    Motivo do Cancelamento:
                  </Typography>
                  <Typography variant='body2' color='error'>
                    {shipment.cancellation_reason}
                  </Typography>
                  <Typography variant='caption' color='textSecondary'>
                    Cancelado em: <FormatDate>{shipment.cancelled_at}</FormatDate> por{' '}
                    {shipment.cancelled_by_name}
                  </Typography>
                </Box>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default function ShipmentsTab({ data }) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const shipments = data.shipments || [];

  const filteredShipments = shipments.filter((shipment) => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      shipment.shipment_number?.toLowerCase().includes(searchLower) ||
      shipment.shipping_method?.toLowerCase().includes(searchLower) ||
      shipment.tracking_code?.toLowerCase().includes(searchLower) ||
      shipment.carrier_name?.toLowerCase().includes(searchLower) ||
      shipment.status?.toLowerCase().includes(searchLower)
    );
  });

  const paginatedShipments = filteredShipments.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage,
  );

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  if (shipments.length === 0) {
    return (
      <Box p={3} textAlign='center'>
        <LocalShipping style={{ fontSize: 64, color: '#ccc', marginBottom: 16 }} />
        <Typography variant='h6' color='textSecondary'>
          Nenhuma expedição encontrada
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          As expedições criadas no período selecionado aparecerão aqui
        </Typography>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <TextField
        fullWidth
        variant='outlined'
        placeholder='Buscar por número, método, rastreio...'
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position='start'>
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        style={{ marginBottom: 16 }}
      />

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell />
              <TableCell>Número</TableCell>
              <TableCell>Método</TableCell>
              <TableCell>Rastreio/Transportadora</TableCell>
              <TableCell>Volumes</TableCell>
              <TableCell>Peso Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Data</TableCell>
              <TableCell>Criado Por</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedShipments.map((shipment) => (
              <ShipmentRow key={shipment.id} shipment={shipment} />
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component='div'
          count={filteredShipments.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage='Linhas por página:'
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} de ${count !== -1 ? count : `mais de ${to}`}`
          }
        />
      </TableContainer>
    </Box>
  );
}
